"use client";

import { useTranslation } from "@/app/i18n/client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "../../../ui/input";
import { cn } from "@/lib/utils";
import LocaleLink from "@/components/locale-link";
import { Button } from "../../../ui/button";
import * as actions from "@/actions";
import { useEffect, useState } from "react";
import {
    SignInEmailValidatorSchema,
    signinEmailValidatorSchema,
    signinPhoneValidatorSchema,
    SignInPhoneValidatorSchema,
} from "@/lib/validators/signin-email";
import OTPFormSignup from "../otp-form-signup";
import { CallAPI_SignIn } from "@/util/services/auth/api";
import { sendOTP, sendOTPSignup, sendPhoneOTP } from "@/actions";
import DividerHorizontal from "@/components/divider-horizontal";
import { CallAPI_UrlAuthFB, CallAPI_UrlAuthGoogle } from "@/util/services/auth/api";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useContext } from "react";
import OTPFormSignin from "../otp-form-signin";
import { toast } from "react-hot-toast";
interface SignInFormProps {
    children?: React.ReactNode;
    guestId?: string;
    sso?: string;
    callbackUrl?: string;
    config?: any;
    isFailed?: (reason?: "email" | "phone") => void;
    radiusStyle: any;
    consentDetail?: any;
    isUseConsent?: any;
}

const SignInNewForm: React.FC<SignInFormProps> = ({
    children,
    guestId,
    sso,
    callbackUrl,
    config,
    isFailed,
    radiusStyle,
    consentDetail,
    isUseConsent,
}) => {
    const { t } = useTranslation();
    const [signInMethod, setSignInMethod] = useState<"email" | "phone">(
        "email",
    );
    const [isLoading, setLoading] = useState<boolean>(false);
    const [otpVisible, setOtpVisible] = useState<boolean>(false);
    const [isRefCode, setIsRefCode] = useState<string>("");
    const [isRef, setIsRef] = useState<string>("");
    const [isEmail, setEmail] = useState<string>("");
    const [isPhone, setPhone] = useState<string>("");
    const [isVerify, setVerify] = useState<boolean>(false);
    const [isPassword, setPassword] = useState<string>("");
    const [urlAuthFB, setUrlAuthFB] = useState<string>("");
    const [urlAuthGoogle, setUrlAuthGoogle] = useState<string>("");
    const [isConsent, setIsConsent] = useState<boolean>(
        isUseConsent && consentDetail && consentDetail.body_html ? false : true,
    );
    const [isSignInSuccess, setIsSignInSuccess] = useState<boolean>(false);
    const systemConfig = config;
    const colorStyle = useContext(ColorStyleContext);

    useEffect(() => {
        const fetchUrl = async () => {
            const urlAuthFB = await CallAPI_UrlAuthFB();
            setUrlAuthFB(urlAuthFB.result);
            const urlAuthGoogle = await CallAPI_UrlAuthGoogle();
            setUrlAuthGoogle(urlAuthGoogle.result);
        };
        fetchUrl();
    }, []);

    const form = useForm<SignInEmailValidatorSchema>({
        resolver: zodResolver(signinEmailValidatorSchema),
        mode: "onChange",
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const phone_form = useForm<SignInPhoneValidatorSchema>({
        resolver: zodResolver(signinPhoneValidatorSchema),
        mode: "onChange",
        defaultValues: {
            phone_number: "",
        },
    });

    const onPhoneSubmit = async (values: SignInPhoneValidatorSchema) => {
        setLoading(true);
        try {
            // Validate Thai mobile format before login
            // Allowed: 010xxxxxxx, 06xxxxxxxx to 09xxxxxxxx
            if (!/^0(10|[6-9]\d)\d{7}$/.test(values.phone_number)) {
                toast.error(`${t("login.phone_number_invalid")}`, {
                    position: "bottom-center",
                    duration: 3000,
                    style: { zIndex: 99999 },
                });
                setLoading(false);
                return;
            }
            const response = await CallAPI_SignIn({
                username: values.phone_number,
                phoneNumber: values.phone_number,
                authFlow: "phone",
            });

            // Check if user exists and is verified
            // If phone is not verified or user doesn't exist, show error
            if (!response?.success || response?.error?.length > 0) {
                toast.error(`${t("login.phone_number_invalid")}`, {
                    position: "bottom-center",
                    duration: 3000,
                    style: { zIndex: 99999 },
                });
                if (isFailed) isFailed("phone");
                setLoading(false);
                return;
            }

            if (response?.result) {
                setPhone(values.phone_number);

                const responseOTP = await sendPhoneOTP(values.phone_number); // request OTP every time
                if (responseOTP.statusCode === 429 || responseOTP.error?.[0] === "OTP_REQUEST_RATE_LIMITED") {
                    const errorKey = `signup.${responseOTP.error?.[0] || "signup.signup_error"}`;
                    toast.error(`${t(errorKey as any)}`, {
                        position: "bottom-center",
                        duration: 3000,
                    });
                    setLoading(false);
                    return;
                }
                if (responseOTP?.result?.success) {
                    setIsRefCode(responseOTP.result.refCode);
                    setIsRef(responseOTP.result.ref);
                    setOtpVisible(true);
                    setLoading(false);
                    return; // Exit early if showing OTP
                }
                // OTP request failed
                if (isFailed) isFailed("phone");
                setLoading(false);
                return;
            }

            // User not found / invalid phone
            if (isFailed) isFailed("phone");
            setLoading(false);
        } catch (err) {
            if (isFailed) isFailed("phone");
            setLoading(false);
        }
    };

    const onOTPSubmit = async (isSuccess?: boolean) => {
        if (!isSuccess) return;
        setLoading(true);
        if (signInMethod === "email") {
            actions
                .signInWithCredentials({
                    email: isEmail || form.getValues("email"),
                    password: isPassword || form.getValues("password"),
                    authFlow: "email",
                    callbackUrl: callbackUrl,
                })
                .then(() => {
                    setIsSignInSuccess(true);
                })
                .catch((error: any) => {
                    if (isFailed) isFailed("email");
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoading(false);
                    }, 3000);
                });
        } else {
            actions
                .signInWithCredentials({
                    phoneNumber: isPhone,
                    authFlow: "phone",
                    callbackUrl: callbackUrl,
                })
                .then(() => {
                    setIsSignInSuccess(true);
                })
                .catch((error: any) => {
                    if (isFailed) isFailed("phone");
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoading(false);
                    }, 3000);
                });
        }
    };

    const onSubmit = async (values: SignInEmailValidatorSchema) => {
        setLoading(true);
        const response = await CallAPI_SignIn({
            username: values.email,
            password: values.password,
            authFlow: "email",
        });
        
        
        // If enableVerifyEmail is enabled, check verification status
        if (systemConfig?.enableVerifyEmail) {
            // Only show OTP if explicitly isVerify is false
            if (response?.result?.isVerify === false) {
                setEmail(values.email);
                setPassword(values.password);
                const responseOTP = await sendOTPSignup(values.email);
                if (responseOTP.result.success) {
                    setIsRefCode(responseOTP.result.refCode);
                    setIsRef(responseOTP.result.ref);
                    setOtpVisible(true);
                    setLoading(false);
                    return; // Exit early if showing OTP
                }
            }
        }

        // If API returns success (even with null result), or isVerify is true, or verifyEmail is disabled
        // proceed with normal login
        if (response?.success) {
            actions
                .signInWithCredentials({
                    email: values.email,
                    password: values.password,
                    authFlow: "email",
                    callbackUrl: callbackUrl,
                })
                .then(() => {
                    setIsSignInSuccess(true);
                })
                .catch((error: any) => {
                    if (isFailed) isFailed("email");
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoading(false);
                    }, 3000);
                });
        } else {
            // API failed
            if (isFailed) {
                isFailed();
            } else {
                setLoading(false);
            }
        }
    };

    const boostrap4 = `
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap-utilities.min.css">
    `;

    const handleInputConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setIsConsent(checked);
    };

    return (
        <>
            {isSignInSuccess ? (
                <div className="flex flex-col items-center p-5 gap-2">
                    <CheckCircle2 size={80} strokeWidth={1} className="text-green-500" />
                    <span className="font-normal text-2xl text-black">{t("login.login_done")}</span>
                    <LocaleLink
                        href="/"
                        className="font-normal text-sm dynamic-color"
                        style={
                            {
                                "--dynamic-color": colorStyle?.link || "#009eca",
                                "--dynamic-hover-color": colorStyle?.linkHover || "#254b7c",
                                "--dynamic-focus-color": colorStyle?.linkHover || "#254b7c",
                            } as React.CSSProperties
                        }>
                        {t("login.login_btn_home")}
                    </LocaleLink>
                </div>
            ) : otpVisible ? (
                signInMethod === "phone" ? (
                    <OTPFormSignin
                        numDigits={6}
                        type="mobile"
                        value={isPhone}
                        data={{
                            ref: isRef,
                            transId: isRef,
                            refCode: isRefCode,
                        }}
                        radiusStyle={radiusStyle}
                        onOTPSubmit={(isSuccess) => onOTPSubmit(isSuccess)}
                    />
                ) : (
                    <OTPFormSignin
                        type="email"
                        value={form.getValues("email")}
                        numDigits={6}
                        data={{
                            ref: isRef,
                            transId: isRef,
                            refCode: isRefCode,
                        }}
                        radiusStyle={radiusStyle}
                        onOTPSubmit={(isSuccess) => onOTPSubmit(isSuccess)}
                    />
                )
            ) : (
                <>
                    {/* Only show tabs if both methods are available */}
                    {systemConfig?.enablePhoneOTP !== false && systemConfig?.enablePhoneOTP !== "email" && (
                        <div className="flex w-full justify-center gap-4 border-b">
                            <button
                                className={`px-4 py-2 font-medium ${
                                    signInMethod === "email"
                                        ? "border-b-2 border-blue-500 text-blue-600"
                                        : "text-gray-500"
                                }`}
                                onClick={() => setSignInMethod("email")}>
                                {t("signup.signup_tab_email")}
                            </button>

                            <button
                                className={`px-4 py-2 font-medium ${
                                    signInMethod === "phone"
                                        ? "border-b-2 border-blue-500 text-blue-600"
                                        : "text-gray-500"
                                }`}
                                onClick={() => setSignInMethod("phone")}>
                                {t("signup.signup_tab_phone")}
                            </button>
                        </div>
                    )}
                    {/* Show email form if method is email OR if only email is enabled */}
                    {signInMethod === "email" || systemConfig?.enablePhoneOTP === "email" ? (
                        <Form key="email" {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                                <FormField
                                    control={form.control}
                                    // name="username"
                                    name="email"
                                    render={({ field, formState }) => (
                                        <FormItem>
                                            <FormLabel
                                                className="form-input-label"
                                                style={{
                                                    color: colorStyle?.text || "#3d3d3d",
                                                }}>
                                                {t("login.login_input_id")}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t("login.login_input_id")}
                                                    {...field}
                                                    value={field.value || ""}
                                                    className={cn(
                                                        "form-input",
                                                        formState.errors.email && "form-input-error",
                                                    )}
                                                />
                                            </FormControl>
                                            <FormMessage translate="yes" />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field, formState }) => (
                                        <FormItem>
                                            <div className="flex justify-between">
                                                <FormLabel
                                                    className="form-input-label"
                                                    style={{
                                                        color: colorStyle?.text || "#3d3d3d",
                                                    }}>
                                                    {t("login.login_input_password")}
                                                </FormLabel>
                                                <LocaleLink
                                                    href="/forgot-password"
                                                    tabIndex={-1}
                                                    className="text-lg font-normal dynamic-color dynamic-hover-color"
                                                    style={
                                                        {
                                                            "--dynamic-color": colorStyle?.link || "#009eca",
                                                            "--dynamic-hover-color": colorStyle?.linkHover || "#254b7c",
                                                            "--dynamic-focus-color": colorStyle?.linkHover || "#254b7c",
                                                        } as React.CSSProperties
                                                    }>
                                                    {t("login.login_btn_forgot")}
                                                </LocaleLink>
                                            </div>
                                            <FormControl>
                                                <Input
                                                    type="password"
                                                    placeholder={t("login.login_input_password")}
                                                    {...field}
                                                    value={field.value || ""}
                                                    className={cn(
                                                        "form-input",
                                                        formState.errors.password && "form-input-error",
                                                    )}
                                                />
                                            </FormControl>
                                            <FormMessage translate="yes" />
                                        </FormItem>
                                    )}
                                />

                                {isUseConsent && consentDetail && consentDetail.body_html && (
                                    <div className="w-full flex flex-row gap-4">
                                        <div
                                            id="consent-checkbox-container"
                                            key="consent-checkbox"
                                            className="flex items-start space-x-2"
                                            style={{ color: colorStyle.text || "#3d3d3d" }}>
                                            <input
                                                type="checkbox"
                                                id="consent-checkbox"
                                                name="consent-checkbox"
                                                value={isConsent ? "true" : "false"}
                                                onChange={handleInputConsentChange}
                                                className="relative top-[3px]"
                                                disabled={isLoading}
                                            />
                                            <label
                                                id="consent-checkbox-label"
                                                htmlFor="consent-checkbox"
                                                className="text-sm">
                                                <div
                                                    id="consent-checkbox-label-content"
                                                    className="relative w-full overflow-hidden"
                                                    style={{
                                                        color: colorStyle?.text || "#3d3d3d",
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: `${boostrap4} ${consentDetail.body_html}` || "",
                                                    }}></div>
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <div className="w-full flex flex-col items-center">
                                    <button
                                        id="btn-login"
                                        className={`w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color flex items-center justify-center text-lg font-normal ${
                                            !isConsent ? "cursor-not-allowed opacity-50" : ""
                                        } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                                        type="submit"
                                        disabled={isLoading || !isConsent}
                                        aria-disabled={isLoading || !isConsent}
                                        style={
                                            {
                                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-bg":
                                                    !isConsent || isLoading
                                                        ? colorStyle?.buttonBgNormal || "#009eca"
                                                        : colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                "--dynamic-hover-color":
                                                    !isConsent || isLoading
                                                        ? colorStyle?.buttonTextNormal || "#ffffff"
                                                        : colorStyle?.buttonTextHover || "#ffffff",
                                                "--dynamic-focus-color":
                                                    !isConsent || isLoading
                                                        ? colorStyle?.buttonTextNormal || "#ffffff"
                                                        : colorStyle?.buttonTextHover || "#ffffff",
                                                borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                                borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                                borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                                borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                            } as React.CSSProperties
                                        }>
                                        <div className="flex items-center justify-center gap-2">
                                            {isLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                t("login.login_btn_login")
                                            )}
                                        </div>
                                    </button>
                                </div>
                            </form>
                        </Form>
                    ) : systemConfig?.enablePhoneOTP === false || systemConfig?.enablePhoneOTP === "email" ? null : (
                        <Form key="mobile" {...phone_form}>
                            <form onSubmit={phone_form.handleSubmit(onPhoneSubmit)} className="space-y-4 p-6">
                                <FormField
                                    control={phone_form.control}
                                    name="phone_number"
                                    render={({ field, formState }) => (
                                        <FormItem>
                                            <FormLabel
                                                className="form-input-label"
                                                style={{
                                                    color: colorStyle?.text || "#3d3d3d",
                                                }}>
                                                {t("login.login_input_phone_number")}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t("login.login_input_phone_number")}
                                                    {...field}
                                                    value={field.value || ""}
                                                    className={cn(
                                                        "form-input",
                                                        formState.errors.phone_number && "form-input-error",
                                                    )}
                                                />
                                            </FormControl>
                                            <FormMessage translate="yes" />
                                        </FormItem>
                                    )}
                                />
                                {isUseConsent && consentDetail && consentDetail.body_html && (
                                    <div className="w-full flex flex-row gap-4">
                                        <div
                                            id="consent-checkbox-container"
                                            key="consent-checkbox"
                                            className="flex items-start space-x-2"
                                            style={{ color: colorStyle.text || "#3d3d3d" }}>
                                            <input
                                                type="checkbox"
                                                id="consent-checkbox"
                                                name="consent-checkbox"
                                                value={isConsent ? "true" : "false"}
                                                onChange={handleInputConsentChange}
                                                className="relative top-[3px]"
                                                disabled={isLoading}
                                            />
                                            <label
                                                id="consent-checkbox-label"
                                                htmlFor="consent-checkbox"
                                                className="text-sm">
                                                <div
                                                    id="consent-checkbox-label-content"
                                                    className="relative w-full overflow-hidden"
                                                    style={{
                                                        color: colorStyle?.text || "#3d3d3d",
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: `${boostrap4} ${consentDetail.body_html}` || "",
                                                    }}></div>
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <div className="w-full flex flex-col items-center">
                                    <button
                                        id="btn-login"
                                        className={`w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color flex items-center justify-center text-lg font-normal  ${
                                            !isConsent ? "cursor-not-allowed opacity-50" : ""
                                        } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                                        type="submit"
                                        aria-disabled={isLoading || !isConsent}
                                        disabled={isLoading || !isConsent}
                                        style={
                                            {
                                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                                borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                                borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                                borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                            } as React.CSSProperties
                                        }>
                                        <div className="flex items-center justify-center gap-2">
                                            {isLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                t("login.login_btn_login")
                                            )}
                                        </div>
                                    </button>
                                </div>
                            </form>
                        </Form>
                    )}

                    {(systemConfig?.enableFacebookAuth || systemConfig?.enableGoogleAuth) && (
                        <div className="flex flex-col px-6 pb-6">
                            <div className="flex flex-row gap-4">
                                <DividerHorizontal />
                                <h4 className="grow font-normal text-sm text-gray-500 w-fit text-center content-center whitespace-nowrap">
                                    {t("login.or_login_with")}
                                </h4>
                                <DividerHorizontal />
                            </div>

                            <div className="flex flex-col sm:flex-col px-6 gap-5">
                                {systemConfig?.enableGoogleAuth && (
                                    <a className="w-full" href={urlAuthGoogle}>
                                        <button
                                            type="submit"
                                            className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                            style={
                                                {
                                                    "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                                    borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                                    borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                                    borderBottomLeftRadius:
                                                        radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                                    borderBottomRightRadius:
                                                        radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                                } as React.CSSProperties
                                            }>
                                            <img
                                                src="/assets/icons/google-login.svg"
                                                alt="Google"
                                                className="h-[18px] w-[18px]"
                                            />
                                            {t("login.sign_in_with_google")}
                                        </button>
                                    </a>
                                )}
                                {systemConfig?.enableFacebookAuth && (
                                    <a className="w-full" href={urlAuthFB}>
                                        <button
                                            type="submit"
                                            className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                            style={
                                                {
                                                    "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                                    borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                                    borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                                    borderBottomLeftRadius:
                                                        radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                                    borderBottomRightRadius:
                                                        radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                                } as React.CSSProperties
                                            }>
                                            <img
                                                src="/assets/icons/facebook-login.svg"
                                                alt="Facebook"
                                                className="h-[18px] w-[18px]"
                                            />
                                            {t("login.sign_in_with_facebook")}
                                        </button>
                                    </a>
                                )}
                            </div>

                            <div className="w-full flex items-center justify-center mt-6">
                                <h4 className="font-normal text-lg w-fit text-center dynamic-color content-center whitespace-nowrap" style={{
                                    color : colorStyle?.text || "#3d3d3d"}}>
                                    <span className="text-gray-500">{t("signup.signup_newmember")}</span>&nbsp;
                                    <LocaleLink href={callbackUrl ? `/signup?callbackUrl=${encodeURIComponent(callbackUrl)}` : "/signup"} className="dynamic-color dynamic-hover-color font-bold" style={{
                                        "--dynamic-color": colorStyle?.link || "#009eca",
                                        "--dynamic-hover-color": colorStyle?.linkHover || "#254b7c",
                                    } as React.CSSProperties}>
                                        {t("signup.signup_register")}
                                    </LocaleLink>
                                </h4>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default SignInNewForm;
